import type { InferOutput } from 'valibot'
import {
  array,
  check,
  integer,
  literal,
  maxLength,
  maxValue,
  minLength,
  minValue,
  nullish,
  number,
  object,
  optional,
  picklist,
  pipe,
  regex,
  strictObject,
  string,
  union,
  uuid,
} from 'valibot'

/**
 * A marking is a highlight, not an excerpt: it holds an ordinary paragraph of
 * law or commentary, and longer passages are marked in parts. The only
 * product-level size rule; the segment and page bounds below are structural
 * guards against unbounded payloads.
 */
export const MARKING_TEXT_MAX = 2_000
/** Pages one segment may span (books). Structural bound, not a product rule. */
export const MARKING_PAGES_MAX = 20
/** Content units one marking may span. Structural bound, not a product rule. */
export const MARKING_SEGMENTS_MAX = 64
// Headroom for the markup, not a second size rule: measured through capture,
// prose runs ~1.3x the text length and dense tables/lists ~6.5x. Derived so a
// fixed cap can't silently start dropping formatting when the rule moves.
export const MARKING_HTML_MAX = MARKING_TEXT_MAX * 8
export const MARKING_NOTE_MAX = 16_384
export const MARKING_CONTEXT_MAX = 1_024
export const MARKING_LABEL_MAX = 512
export const MARKING_URL_MAX = 2_048
export const MARKING_PAYLOAD_BYTES_MAX = 160 * 1024
export const markingColorSchema = picklist(['polar', 'blurple', 'moss', 'alert', 'warning'])
export const markingContentTypeSchema = picklist(['law', 'commentary', 'book', 'academic-article', 'journal-article'])
export type MarkingContentType = InferOutput<typeof markingContentTypeSchema>

/** Single source of the palette: the app layer keys its styling off these ids. */
export const MARKING_COLORS = markingColorSchema.options
export type MarkingColor = InferOutput<typeof markingColorSchema>
const boundedString = (maximum: number) => pipe(string(), maxLength(maximum))
const identifier = pipe(string(), minLength(1), maxLength(MARKING_LABEL_MAX))
const offset = pipe(number(), integer(), minValue(0), maxValue(Number.MAX_SAFE_INTEGER))
const version = pipe(number(), integer(), minValue(1), maxValue(Number.MAX_SAFE_INTEGER - 1))

export function isLocalMarkingPath(path: string): boolean {
  return /^\/(?!\/)/.test(path) && !/[\\\s\p{Cc}]/u.test(path)
}

const citationEntries = {
  source: picklist(['law', 'literature', 'article', 'commentary']),
  title: optional(string()),
  url: optional(string()),
  authors: optional(array(string())),
  publisher: optional(string()),
  year: optional(union([string(), number()])),
  page: optional(union([string(), number()])),
  lawId: optional(string()),
  paragraphTitle: optional(string()),
  timestamp: optional(string()),
  lawTimestamp: optional(string()),
  dateOfPublication: optional(string()),
  dateInForce: optional(string()),
  lastChangeInForce: optional(string()),
  shortTitle: optional(string()),
  commentaryTitle: optional(string()),
  lastUpdated: optional(string()),
  lawFragmentId: optional(string()),
  chapterTitle: optional(string()),
  editionId: optional(string()),
  originalPublicationDate: optional(string()),
  legalAreas: optional(array(string())),
  authorSlugs: optional(array(string())),
  isbn: optional(array(string())),
  editionNumber: optional(string()),
  volume: optional(string()),
  issue: optional(string()),
  journalTitle: optional(string()),
  firstPage: optional(union([string(), number()])),
  lastPage: optional(union([string(), number()])),
  paragraph: optional(string()),
  startPage: optional(union([string(), number()])),
}

export const markingCitationSchema = object(citationEntries)
const citationWriteSchema = pipe(
  strictObject(citationEntries),
  check(
    (citation) => new TextEncoder().encode(JSON.stringify(citation)).byteLength <= 16 * 1024,
    'Citation exceeds its byte budget',
  ),
)

export const markingSectionSchema = strictObject({
  kind: picklist(['toc', 'unit', 'heading']),
  nodeType: optional(identifier),
  title: pipe(string(), minLength(1), maxLength(MARKING_LABEL_MAX)),
  unitId: optional(identifier),
  anchor: optional(identifier),
  href: optional(boundedString(MARKING_URL_MAX)),
})
export type MarkingSection = InferOutput<typeof markingSectionSchema>

export const markingSourceSchema = strictObject({
  contentType: markingContentTypeSchema,
  workId: optional(identifier),
  documentId: identifier,
  rootDocumentId: optional(identifier),
  unitId: identifier,
  ancestorIds: pipe(array(identifier), maxLength(32)),
  sectionPath: optional(pipe(array(markingSectionSchema), maxLength(64))),
  headingId: optional(identifier),
  about: optional(
    strictObject({
      lawId: identifier,
      unitId: optional(identifier),
      dateInForce: optional(boundedString(64)),
      lastChangeInForce: optional(boundedString(64)),
      sourceTimestamp: optional(boundedString(64)),
    }),
  ),
  revision: strictObject({
    dateInForce: optional(boundedString(64)),
    lastChangeInForce: optional(boundedString(64)),
    editorialUpdatedAt: optional(boundedString(64)),
    rootEditorialUpdatedAt: optional(boundedString(64)),
    publishedAt: optional(boundedString(64)),
    editionId: optional(identifier),
    previousEditionId: optional(identifier),
    sourceTimestamp: optional(boundedString(64)),
  }),
})

export const markingAnchorSchema = pipe(
  strictObject({
    normalizationVersion: literal(1),
    unitTextFingerprint: pipe(string(), regex(/^[a-f0-9]{64}$/)),
    startOffset: offset,
    endOffset: offset,
  }),
  check((anchor) => anchor.endOffset > anchor.startOffset, 'Invalid selection offsets'),
)

const markingSegmentsSchema = pipe(
  array(strictObject({
    unitId: identifier,
    // Commentary gives every comment its own documentId, so a marking spanning
    // comments needs it per segment to re-find its root. Absent on records
    // written before cross-unit markings covered anything but books.
    documentId: optional(identifier),
    pages: pipe(array(identifier), maxLength(MARKING_PAGES_MAX)),
    text: pipe(string(), minLength(1), maxLength(MARKING_TEXT_MAX)),
    textBefore: boundedString(MARKING_CONTEXT_MAX),
    textAfter: boundedString(MARKING_CONTEXT_MAX),
    anchor: markingAnchorSchema,
  })),
  minLength(1),
  maxLength(MARKING_SEGMENTS_MAX),
  check(segments => segments.reduce((length, segment) => length + segment.text.length, 0) <= MARKING_TEXT_MAX, 'Selection is too long'),
  check(segments => new Set(segments.map(segment => segment.unitId)).size === segments.length, 'Duplicate selection units'),
)

export const markingSchema = object({
  id: string(),
  color: string(),
  text: string(),
  html: optional(string()),
  textBefore: optional(string(), ''),
  textAfter: optional(string(), ''),
  pageUrl: string(),
  pageHash: nullish(string()),
  citation: optional(markingCitationSchema),
  sectionTitle: nullish(string()),
  sectionAnchor: nullish(string()),
  note: nullish(string()),
  createdAt: string(),
  position: nullish(number()),
  schemaVersion: literal(2),
  source: markingSourceSchema,
  anchor: markingAnchorSchema,
  segments: optional(markingSegmentsSchema),
})

export const markingCreateSchema = pipe(strictObject({
  id: pipe(string(), uuid()),
  color: markingColorSchema,
  text: pipe(string(), minLength(1), maxLength(MARKING_TEXT_MAX)),
  html: optional(boundedString(MARKING_HTML_MAX)),
  textBefore: optional(boundedString(MARKING_CONTEXT_MAX), ''),
  textAfter: optional(boundedString(MARKING_CONTEXT_MAX), ''),
  pageUrl: pipe(
    boundedString(MARKING_URL_MAX),
    check(isLocalMarkingPath, 'Expected a local content path'),
  ),
  pageHash: nullish(boundedString(MARKING_LABEL_MAX)),
  citation: optional(citationWriteSchema),
  sectionTitle: nullish(boundedString(MARKING_LABEL_MAX)),
  sectionAnchor: nullish(boundedString(MARKING_LABEL_MAX)),
  note: nullish(boundedString(MARKING_NOTE_MAX)),
  position: nullish(offset),
  schemaVersion: literal(2),
  source: markingSourceSchema,
  anchor: markingAnchorSchema,
  segments: optional(markingSegmentsSchema),
}), check(marking => !marking.segments || (
  marking.source.unitId === marking.segments[0]?.unitId
  && marking.text === marking.segments.map(segment => segment.text).join('')
), 'Invalid selection segments'))

export const markingPatchSchema = pipe(
  strictObject({
    color: optional(markingColorSchema),
    note: optional(nullish(boundedString(MARKING_NOTE_MAX))),
    expectedVersion: version,
  }),
  check(
    (patch) => patch.color !== undefined || patch.note !== undefined,
    'Patch must change colour or note',
  ),
)
export const markingDeleteSchema = strictObject({ expectedVersion: version })
export const markingParamsSchema = strictObject({ id: pipe(string(), uuid()) })

export const storedMarkingSchema = object({
  data: markingSchema,
  recordVersion: version,
  updatedAt: string(),
})
export const markingPageSchema = object({
  markings: array(storedMarkingSchema),
  cursor: nullish(string()),
})

export type MarkingRecord = InferOutput<typeof markingSchema>
export type MarkingSource = InferOutput<typeof markingSourceSchema>
export type MarkingAnchor = InferOutput<typeof markingAnchorSchema>
export type MarkingCreateInput = InferOutput<typeof markingCreateSchema>
export type MarkingPatch = InferOutput<typeof markingPatchSchema>
export type StoredMarking = InferOutput<typeof storedMarkingSchema>
export type MarkingPage = InferOutput<typeof markingPageSchema>
